import React, { useState, useEffect } from 'react';
import {
  BanknotesIcon,
  PlusIcon,
  MinusIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  InformationCircleIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import apiClient from '../utils/apiClient';

interface Ruecklage {
  id: number;
  name: string;
  typ: 'zweckgebunden' | 'wiederbeschaffung' | 'frei' | 'betriebsmittel';
  zweck?: string;
  ziel_betrag?: number;
  ziel_datum?: string;
  aktueller_stand: number;
}

interface RuecklagenSummary {
  ruecklagen: Ruecklage[];
  total: number;
  jahr: number;
}

interface RuecklagenCardProps {
  isVorstand?: boolean;
  onRefresh?: () => void;
}

const typLabels: Record<string, string> = {
  zweckgebunden: 'Zweckgebunden',
  wiederbeschaffung: 'Wiederbeschaffung',
  frei: 'Freie Rücklage',
  betriebsmittel: 'Betriebsmittel'
};

const typColors: Record<string, string> = {
  zweckgebunden: 'bg-info-100 text-info-800 dark:bg-info-900/30 dark:text-info-300',
  wiederbeschaffung: 'bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-300',
  frei: 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-300',
  betriebsmittel: 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300'
};

const RuecklagenCard: React.FC<RuecklagenCardProps> = ({ isVorstand = false, onRefresh }) => {
  const [data, setData] = useState<RuecklagenSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedRuecklage, setSelectedRuecklage] = useState<Ruecklage | null>(null);
  const [bookingType, setBookingType] = useState<'zufuehrung' | 'entnahme'>('zufuehrung');

  // Form state für neue Rücklage
  const [formData, setFormData] = useState({
    name: '',
    typ: 'betriebsmittel' as Ruecklage['typ'],
    zweck: '',
    ziel_betrag: '',
    ziel_datum: ''
  });

  // Booking form state
  const [bookingAmount, setBookingAmount] = useState('');
  const [bookingKommentar, setBookingKommentar] = useState('');

  const fetchData = async () => {
    try {
      const response = await apiClient.get('/api/ruecklagen/summary');
      if (response.data.success) {
        setData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching ruecklagen:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Prevent background scroll when any modal is open
  useEffect(() => {
    if (showAddModal || showBookingModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showAddModal, showBookingModal]);

  const handleAddRuecklage = async () => {
    try {
      await apiClient.post('/api/ruecklagen', {
        name: formData.name,
        typ: formData.typ,
        zweck: formData.zweck || null,
        ziel_betrag: formData.ziel_betrag ? parseFloat(formData.ziel_betrag) : null,
        ziel_datum: formData.ziel_datum || null
      });
      setShowAddModal(false);
      setFormData({ name: '', typ: 'betriebsmittel', zweck: '', ziel_betrag: '', ziel_datum: '' });
      fetchData();
      onRefresh?.();
    } catch (error) {
      console.error('Error creating ruecklage:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Rücklage wirklich deaktivieren?')) return;
    try {
      await apiClient.delete(`/api/ruecklagen/${id}`);
      fetchData();
      onRefresh?.();
    } catch (error) {
      console.error('Error deleting ruecklage:', error);
    }
  };

  const openBookingModal = (ruecklage: Ruecklage, type: 'zufuehrung' | 'entnahme') => {
    setSelectedRuecklage(ruecklage);
    setBookingType(type);
    setBookingAmount('');
    setBookingKommentar('');
    setShowBookingModal(true);
  };

  const handleBooking = async () => {
    if (!selectedRuecklage || !bookingAmount) return;

    const amount = parseFloat(bookingAmount);
    if (isNaN(amount) || amount <= 0) return;

    try {
      // Get current year values first
      const currentYear = new Date().getFullYear();
      const detailResponse = await apiClient.get(`/api/ruecklagen/${selectedRuecklage.id}`);
      const jahreswerte = detailResponse.data.data?.jahreswerte || [];
      const currentYearData = jahreswerte.find((j: any) => j.jahr === currentYear);

      const anfangsstand = currentYearData?.anfangsstand || 0;
      const existingZufuehrung = currentYearData?.zufuehrung || 0;
      const existingEntnahme = currentYearData?.entnahme || 0;

      await apiClient.post(`/api/ruecklagen/${selectedRuecklage.id}/jahreswert`, {
        jahr: currentYear,
        anfangsstand: anfangsstand,
        zufuehrung: bookingType === 'zufuehrung' ? existingZufuehrung + amount : existingZufuehrung,
        entnahme: bookingType === 'entnahme' ? existingEntnahme + amount : existingEntnahme,
        kommentar: bookingKommentar || null
      });

      setShowBookingModal(false);
      setSelectedRuecklage(null);
      setBookingAmount('');
      setBookingKommentar('');
      fetchData();
      onRefresh?.();
    } catch (error) {
      console.error('Error booking:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-[#1c2432] rounded-xl border border-gray-200 dark:border-gray-700 p-4 animate-pulse">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-[#1c2432] rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header + Summary combined */}
      <div className="p-3 sm:p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center">
              <BanknotesIcon className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Rücklagen
            </h3>
            <button
              onClick={() => setShowInfo(!showInfo)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <InformationCircleIcon className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="flex items-center gap-1.5">
            {isVorstand && (
              <button
                onClick={() => setShowAddModal(true)}
                className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Neue Rücklage anlegen"
              >
                <PlusIcon className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg"
            >
              {expanded ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* Info Box */}
        {showInfo && (
          <div className="mb-2 p-2.5 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-[11px] text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-800/50">
            <strong>Rücklagen (§ 62 AO)</strong> sind reservierte Beträge für bestimmte Zwecke
            wie Wiederbeschaffung, Betriebsmittel oder zweckgebundene Projekte.
          </div>
        )}

        {/* Summary */}
        <div className="flex items-baseline justify-between">
          <span className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white tabular-nums">
            {formatCurrency(data?.total || 0)}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {data?.ruecklagen.length || 0} Rücklage{(data?.ruecklagen.length || 0) !== 1 ? 'n' : ''}
          </span>
        </div>
      </div>

      {/* Expanded List */}
      {expanded && data?.ruecklagen && data.ruecklagen.length > 0 && (
        <div className="border-t border-gray-100 dark:border-gray-700/50">
          {data.ruecklagen.map((r, idx) => (
            <div
              key={r.id}
              className={`px-3 sm:px-4 py-2.5 sm:py-3 ${idx < data.ruecklagen.length - 1 ? 'border-b border-gray-100 dark:border-gray-700/50' : ''}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-[13px] font-semibold text-gray-900 dark:text-white truncate">{r.name}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${typColors[r.typ]}`}>
                      {typLabels[r.typ]}
                    </span>
                  </div>
                  {r.zweck && (
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate">{r.zweck}</p>
                  )}
                  {r.ziel_betrag && (
                    <div className="mt-1.5">
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1">
                        <div
                          className="bg-amber-500 h-1 rounded-full transition-all"
                          style={{ width: `${Math.min(100, (r.aktueller_stand / r.ziel_betrag) * 100)}%` }}
                        />
                      </div>
                      <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 tabular-nums">
                        {Math.round((r.aktueller_stand / r.ziel_betrag) * 100)}% von {formatCurrency(r.ziel_betrag)}
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                  <span className="text-[13px] font-bold text-gray-900 dark:text-white tabular-nums">
                    {formatCurrency(r.aktueller_stand)}
                  </span>
                  {isVorstand && (
                    <div className="flex items-center gap-0.5">
                      <button
                        onClick={() => openBookingModal(r, 'zufuehrung')}
                        className="p-1 text-success-600 hover:bg-success-100 dark:hover:bg-success-900/30 rounded transition-colors"
                        title="Zuführung"
                      >
                        <ArrowUpIcon className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => openBookingModal(r, 'entnahme')}
                        className="p-1 text-error-600 hover:bg-error-100 dark:hover:bg-error-900/30 rounded transition-colors"
                        title="Entnahme"
                      >
                        <ArrowDownIcon className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(r.id)}
                        className="p-1 text-gray-400 hover:text-error-600 hover:bg-error-100 dark:hover:bg-error-900/30 rounded transition-colors"
                        title="Löschen"
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {expanded && (!data?.ruecklagen || data.ruecklagen.length === 0) && (
        <div className="p-5 text-center text-gray-400 dark:text-gray-500 border-t border-gray-100 dark:border-gray-700/50">
          <BanknotesIcon className="h-8 w-8 mx-auto mb-1.5 opacity-30" />
          <p className="text-xs">Keine Rücklagen vorhanden</p>
          {isVorstand && (
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-1.5 text-primary-600 hover:text-primary-700 text-xs font-medium"
            >
              Erste Rücklage anlegen
            </button>
          )}
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white dark:bg-[#1c2432] rounded-t-2xl sm:rounded-xl max-w-md w-full max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="p-5 sm:p-6 pb-0 sm:pb-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Neue Rücklage anlegen
              </h3>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-5 sm:px-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="z.B. Agility-Parcours"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Typ *
                  </label>
                  <select
                    value={formData.typ}
                    onChange={(e) => setFormData({ ...formData, typ: e.target.value as Ruecklage['typ'] })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="betriebsmittel">Betriebsmittelrücklage</option>
                    <option value="zweckgebunden">Zweckgebunden</option>
                    <option value="wiederbeschaffung">Wiederbeschaffung</option>
                    <option value="frei">Freie Rücklage</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Zweck/Beschreibung
                  </label>
                  <textarea
                    value={formData.zweck}
                    onChange={(e) => setFormData({ ...formData, zweck: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    rows={2}
                    placeholder="Wofür wird gespart?"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Zielbetrag
                    </label>
                    <input
                      type="number"
                      value={formData.ziel_betrag}
                      onChange={(e) => setFormData({ ...formData, ziel_betrag: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="0,00"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Zieldatum
                    </label>
                    <input
                      type="date"
                      value={formData.ziel_datum}
                      onChange={(e) => setFormData({ ...formData, ziel_datum: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Sticky Footer */}
            <div className="flex justify-end gap-3 p-5 sm:p-6 pt-4 sm:pt-4 border-t border-gray-100 dark:border-gray-700/50 pb-8 sm:pb-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Abbrechen
              </button>
              <button
                onClick={handleAddRuecklage}
                disabled={!formData.name}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white rounded-lg"
              >
                Anlegen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {showBookingModal && selectedRuecklage && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white dark:bg-[#1c2432] rounded-t-2xl sm:rounded-xl max-w-md w-full max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="p-5 sm:p-6 pb-0 sm:pb-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {bookingType === 'zufuehrung' ? 'Zuführung' : 'Entnahme'}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {selectedRuecklage.name} · Aktuell: {formatCurrency(selectedRuecklage.aktueller_stand)}
              </p>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-5 sm:px-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Betrag *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={bookingAmount}
                      onChange={(e) => setBookingAmount(e.target.value)}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="0,00"
                      step="0.01"
                      min="0.01"
                      autoFocus
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Kommentar (optional)
                  </label>
                  <input
                    type="text"
                    value={bookingKommentar}
                    onChange={(e) => setBookingKommentar(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="z.B. Aus Überschuss 2025"
                  />
                </div>

                {bookingAmount && parseFloat(bookingAmount) > 0 && (
                  <div className={`p-3 rounded-lg ${bookingType === 'zufuehrung' ? 'bg-success-50 dark:bg-success-900/20' : 'bg-error-50 dark:bg-error-900/20'}`}>
                    <p className="text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Neuer Stand: </span>
                      <span className={`font-semibold ${bookingType === 'zufuehrung' ? 'text-success-700 dark:text-success-300' : 'text-error-700 dark:text-error-300'}`}>
                        {formatCurrency(
                          bookingType === 'zufuehrung'
                            ? selectedRuecklage.aktueller_stand + parseFloat(bookingAmount)
                            : selectedRuecklage.aktueller_stand - parseFloat(bookingAmount)
                        )}
                      </span>
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Sticky Footer */}
            <div className="flex justify-end gap-3 p-5 sm:p-6 pt-4 sm:pt-4 border-t border-gray-100 dark:border-gray-700/50 pb-8 sm:pb-6">
              <button
                onClick={() => {
                  setShowBookingModal(false);
                  setSelectedRuecklage(null);
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Abbrechen
              </button>
              <button
                onClick={handleBooking}
                disabled={!bookingAmount || parseFloat(bookingAmount) <= 0}
                className={`px-4 py-2 text-white rounded-lg ${
                  bookingType === 'zufuehrung'
                    ? 'bg-success-600 hover:bg-success-700 disabled:bg-success-400'
                    : 'bg-error-600 hover:bg-error-700 disabled:bg-error-400'
                }`}
              >
                {bookingType === 'zufuehrung' ? 'Zuführen' : 'Entnehmen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RuecklagenCard;
